import { HttpClient } from '@adapters/HttpClient';
import { AvailableLocation, PackingStation, PackingStationDetail, Printer, TeamChild } from '../models/PackingStation';
import { resolvePackingStationLocationCode } from '../utils/resolvePackingStationLocationCode';

export interface PackingStationsFilters {
  page?: number;
  limit?: number;
  active?: boolean;
  code?: string;
  searchFilter?: { filters: any[]; logic: string };
}

export interface PackingStationsPage {
  packingStations: PackingStation[];
  nextPage?: number;
  hasMore: boolean;
  total: number;
}

// Payload para crear un puesto de packing
export interface CreatePackingStationPayload {
  code: string;
  active: boolean;
}

// Payload para añadir impresora al puesto
export interface AddPrinterPayload {
  printer_code: string;
  printer_id?: number;
  type: '10x15' | 'a4';
}

// Payload para añadir cliente al puesto
export interface AddClientPayload {
  tenant_external_id: number;
}

// Payload para añadir ubicación al puesto
export interface AddLocationPayload {
  code: string;
}

// Payload para actualizar código del puesto
export interface UpdatePackingStationPayload {
  code: string;
}

export class PackingStationsApiAdapter {
  constructor(private httpClient: HttpClient) {}

  // Obtiene el listado paginado de puestos de packing
  async getPackingStationsPaginated(filters: PackingStationsFilters = {}): Promise<PackingStationsPage> {
    const { page = 1, limit = 50, active, code } = filters;

    const params: any = {
      page,
      per_page: limit,
      ...(active !== undefined && { active: active ? 1 : 0 }),
      ...(code && code.trim() !== '' && { code: code.trim() }),
    };

    const showGlobalLoading = page === 1;
    const response = await this.httpClient.get<any>('/packing-stations', params, {}, showGlobalLoading);

    // Laravel paginator: total/current_page/last_page en raíz; otros endpoints usan meta
    const meta = response?.meta;
    const currentPage = meta?.current_page ?? meta?.currentPage ?? response?.current_page ?? response?.currentPage ?? 1;
    const lastPage = meta?.last_page ?? meta?.lastPage ?? response?.last_page ?? response?.lastPage ?? 1;
    const total = meta?.total ?? response?.total ?? 0;

    return {
      packingStations: response?.data ?? [],
      nextPage: currentPage < lastPage ? currentPage + 1 : undefined,
      hasMore: currentPage < lastPage,
      total,
    };
  }

  // Obtiene el detalle de un puesto (con printers, clients, locations)
  async getPackingStationById(id: number): Promise<PackingStationDetail> {
    const raw = await this.httpClient.get<any>(`/packing-stations/${id}`);
    const response =
      raw?.data &&
      typeof raw.data === 'object' &&
      !Array.isArray(raw.data) &&
      (Array.isArray(raw.data.locations) || raw.data.id != null || raw.data.code != null)
        ? raw.data
        : raw;

    // Normalizar relaciones usando datos ya embebidos en la respuesta del backend.
    if (response && Array.isArray(response.locations)) {
      response.locations = response.locations.map((loc: Record<string, unknown>) => ({
        ...loc,
        code: resolvePackingStationLocationCode(loc),
      }));
    }

    if (response && Array.isArray(response.clients)) {
      response.clients = response.clients.map((client: Record<string, unknown>) => {
        const nestedTeam = client.team as Record<string, unknown> | undefined;
        const teamName = typeof nestedTeam?.name === 'string' ? nestedTeam.name.trim() : '';
        const currentName = typeof client.name === 'string' ? client.name.trim() : '';
        return {
          ...client,
          name: currentName || teamName,
        };
      });
    }

    if (response && Array.isArray(response.printers)) {
      response.printers = response.printers.map((printer: Record<string, unknown>) => {
        const nestedPrinter = printer.printer as Record<string, unknown> | undefined;
        const nestedName = typeof nestedPrinter?.name === 'string' ? nestedPrinter.name.trim() : '';
        const nestedCode = typeof nestedPrinter?.code === 'string' ? nestedPrinter.code.trim() : '';
        const currentName = typeof printer.name === 'string' ? printer.name.trim() : '';
        const currentCode = typeof printer.printer_code === 'string' ? printer.printer_code.trim() : '';

        return {
          ...printer,
          name: currentName || nestedName,
          printer_code: currentCode || nestedCode,
        };
      });
    }

    return response as PackingStationDetail;
  }

  // Crea un nuevo puesto de packing
  async createPackingStation(payload: CreatePackingStationPayload): Promise<PackingStation> {
    const response = await this.httpClient.post<PackingStation>('/packing-stations', payload);
    return response;
  }

  // Actualiza el código de un puesto
  async updatePackingStation(id: number, payload: UpdatePackingStationPayload): Promise<PackingStation> {
    const response = await this.httpClient.put<PackingStation>(`/packing-stations/${id}`, payload);
    return response;
  }

  // Activa o desactiva un puesto
  async toggleStatus(id: number, active: boolean): Promise<any> {
    const response = await this.httpClient.patch<any>(`/packing-stations/${id}/status`, { active });
    return response;
  }

  // Añade una impresora al puesto
  async addPrinter(id: number, payload: AddPrinterPayload): Promise<any> {
    const response = await this.httpClient.post<any>(`/packing-stations/${id}/printers`, payload);
    return response;
  }

  // Quita una impresora del puesto
  async removePrinter(id: number, printerRelationId: number): Promise<any> {
    const response = await this.httpClient.delete<any>(`/packing-stations/${id}/printers/${printerRelationId}`);
    return response;
  }

  // Añade un cliente al puesto
  async addClient(id: number, payload: AddClientPayload): Promise<any> {
    const response = await this.httpClient.post<any>(`/packing-stations/${id}/clients`, payload);
    return response;
  }

  // Quita un cliente del puesto
  async removeClient(id: number, tenantExternalId: number): Promise<any> {
    const response = await this.httpClient.delete<any>(`/packing-stations/${id}/clients/${tenantExternalId}`);
    return response;
  }

  // Añade una ubicación al puesto
  async addLocation(id: number, payload: AddLocationPayload): Promise<any> {
    const response = await this.httpClient.post<any>(`/packing-stations/${id}/locations`, payload);
    return response;
  }

  // Elimina una ubicación global por ID
  async deleteLocation(locationId: number): Promise<void> {
    await this.httpClient.delete<any>(`/locations/${locationId}`);
  }

  // Obtiene las impresoras disponibles (activas)
  async getPrinters(): Promise<Printer[]> {
    const response = await this.httpClient.get<any>('/printers', {
      only_active: true,
      per_page: 100,
    });
    return response.data;
  }

  // Obtiene los clientes disponibles (teams-child)
  async getTeamsChild(): Promise<TeamChild[]> {
    const response = await this.httpClient.get<any>('/teams-child', {
      per_page: 100,
    });
    return response.data;
  }

  // Obtiene ubicaciones disponibles para asociar (product_id is null)
  async getAvailableLocations(): Promise<AvailableLocation[]> {
    const response = await this.httpClient.get<any>('/locations', {
      'advanced_filters[logic]': 'AND',
      'advanced_filters[filters][0][column]': 'product_id',
      'advanced_filters[filters][0][operator]': 'is_null',
      per_page: 200,
    });
    return response?.data ?? [];
  }

  // Obtiene ubicaciones tipo Packing Station (type_id=6) vacías y disponibles
  // Nota: type_id va dentro de advanced_filters porque el backend ignora filtros simples
  // cuando se usan advanced_filters
  async getAvailablePackingStationLocations(search?: string): Promise<AvailableLocation[]> {
    const params: Record<string, unknown> = {
      per_page: 50,
      'advanced_filters[filters][0][column]': 'type_id',
      'advanced_filters[filters][0][operator]': 'in',
      'advanced_filters[filters][0][value][]': '6',
      'advanced_filters[filters][1][column]': 'product_id',
      'advanced_filters[filters][1][operator]': 'is_null',
      'advanced_filters[filters][2][column]': 'is_locked',
      'advanced_filters[filters][2][operator]': 'in',
      'advanced_filters[filters][2][value][]': '0',
      'advanced_filters[logic]': 'AND',
    };
    if (search && search.trim()) {
      params.code = search.trim();
    }
    const response = await this.httpClient.get<any>('/locations', params, {}, false);
    return response?.data ?? [];
  }

  // Crea una nueva ubicación global tipo Packing Station vía POST /api/v1/locations
  async createGlobalLocation(payload: { code: string; type_id: number; is_locked: boolean }): Promise<void> {
    await this.httpClient.post<any>('/locations', payload);
  }
}
